/**
 * Sources de données & cohérence — Carte d'identité énergétique
 *
 * Vue synthétique des 5 sources qui alimentent la Carte :
 *   1. Qualification (formulaire client via token)
 *   2. Relevé d'étude sur place (auditeur terrain)
 *   3. Attestations OPERAT (documents officiels)
 *   4. Études (docs bureau d'études)
 *   5. Devis
 *
 * + un petit bloc "Cohérence des sources" qui signale les incohérences
 * évidentes entre ce que le client déclare et ce que le terrain observe.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    ClipboardList, ClipboardCheck, ClipboardX,
    Camera, FileText, FileCheck2, FileSignature,
    AlertTriangle, CheckCircle2, Layers,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

/** Chip d'une source avec statut (present / missing / partial). */
function SourceChip({ icon: Icon, label, count, status, hint, testid, onClick }) {
    const style = {
        present:  'bg-emerald-50 border-emerald-200 text-emerald-900',
        partial:  'bg-amber-50 border-amber-200 text-amber-900',
        missing:  'bg-slate-50 border-slate-200 text-slate-500',
    }[status];
    const dot = {
        present: 'bg-emerald-500',
        partial: 'bg-amber-500',
        missing: 'bg-slate-300',
    }[status];
    const Wrapper = onClick ? 'button' : 'div';
    return (
        <Wrapper
            type={onClick ? 'button' : undefined}
            onClick={onClick}
            data-testid={testid}
            className={cn(
                'flex items-start gap-1.5 sm:gap-2 rounded-lg border px-2 sm:px-3 py-1.5 sm:py-2 text-left transition-colors w-full min-w-0',
                onClick && 'hover:shadow-sm cursor-pointer active:scale-[0.98]',
                style,
            )}
        >
            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5 opacity-70" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wide truncate">{label}</span>
                    <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', dot)} />
                </div>
                <div className="text-[11px] sm:text-xs mt-0.5 font-medium truncate">
                    {count != null ? count : (status === 'present' ? 'Renseigné' : status === 'partial' ? 'Partiel' : 'Absent')}
                </div>
                {hint && <div className="hidden sm:block text-[10px] opacity-70 mt-0.5 truncate">{hint}</div>}
            </div>
        </Wrapper>
    );
}

/** Bloc d'incohérence détectée entre 2 sources. */
function CoherenceIssue({ severity = 'warn', label, detail, testid }) {
    const style = severity === 'error'
        ? 'bg-rose-50 border-rose-200 text-rose-900'
        : 'bg-amber-50 border-amber-200 text-amber-900';
    return (
        <div className={cn('flex items-start gap-2 rounded-md border px-2.5 py-1.5', style)} data-testid={testid}>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <div className="text-xs">
                <span className="font-semibold">{label}</span>
                {detail && <span className="ml-1 opacity-80">— {detail}</span>}
            </div>
        </div>
    );
}

/** Petit helper pour scroller vers une ancre à l'intérieur de la page. */
function scrollTo(id) {
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function EnergySourcesCard({ prospect, bundle }) {
    const { readings = [], equipment = [], attestations = [], principal_site } = bundle || {};
    const [qualif, setQualif] = useState(null);          // chantier-energy-summary
    const [studyCount, setStudyCount] = useState(null);  // relevés d'étude
    const [docs, setDocs] = useState({ etude: 0, devis: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [q, s, d] = await Promise.all([
                    api.get(`/prospects/${prospect.id}/chantier-energy-summary`).then((r) => r.data).catch(() => null),
                    api.get(`/study-forms/prospects/${prospect.id}`).then((r) => r.data).catch(() => []),
                    api.get(`/prospects/${prospect.id}/chantier-docs`).then((r) => r.data).catch(() => []),
                ]);
                if (cancelled) return;
                setQualif(q);
                const studiesLen = Array.isArray(s) ? s.length : (s?.studies?.length ?? s?.count ?? 0);
                setStudyCount(studiesLen);
                const list = Array.isArray(d) ? d : [];
                setDocs({
                    etude: list.filter((x) => x.category === 'etude').length,
                    devis: list.filter((x) => x.category === 'devis').length,
                });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [prospect.id]);

    // Statut de la qualification client
    const qualifStatus = (() => {
        if (!qualif || qualif.status === 'none') return { status: 'missing', count: 'Non initié', hint: 'Envoyer le lien de qualification au client' };
        if (qualif.status === 'validated') return { status: 'present', count: `${qualif.qualification?.display_note}/4`, hint: qualif.qualification?.appreciation?.slice(0, 60) };
        return { status: 'partial', count: `Brouillon · ${qualif.qualification?.completeness || 0}%`, hint: 'En cours de remplissage' };
    })();

    // Statut du relevé d'étude terrain
    const studyStatus = (() => {
        if (studyCount == null) return { status: 'missing', count: '—' };
        if (studyCount === 0) return { status: 'missing', count: 'Aucun relevé', hint: 'À réaliser par l\'auditeur' };
        return { status: 'present', count: `${studyCount} relevé${studyCount > 1 ? 's' : ''}` };
    })();

    // Statut OPERAT — vérification par site principal
    const operatStatus = (() => {
        const forSite = attestations.filter((a) => !a.site_id || a.site_id === principal_site?.id);
        if (attestations.length === 0) return { status: 'missing', count: 'Aucune attestation', hint: 'Uploader le PDF OPERAT' };
        if (forSite.length === 0) return { status: 'partial', count: `${attestations.length} sur autre(s) site(s)` };
        return { status: 'present', count: `${forSite.length} attestation${forSite.length > 1 ? 's' : ''}` };
    })();

    // Statut Études & Devis (documents téléversés)
    const etudeStatus = docs.etude > 0
        ? { status: 'present', count: `${docs.etude} étude${docs.etude > 1 ? 's' : ''}` }
        : { status: 'missing', count: 'Aucune étude' };
    const devisStatus = docs.devis > 0
        ? { status: 'present', count: `${docs.devis} devis` }
        : { status: 'missing', count: 'Aucun devis' };

    // ══════════════════════════════════════════════════════════════════════
    // Détection d'incohérences entre les sources (qualif client vs terrain)
    // ══════════════════════════════════════════════════════════════════════
    const issues = [];
    const answers = qualif?.qualification?.answers || {};
    if (qualif?.status === 'validated') {
        // Groupes froids déclarés mais aucun équipement de ce type sur le patrimoine
        if (answers.groupes_froids_present === 'oui') {
            const hasEquip = equipment.some((e) => (e.category || '').toLowerCase().includes('froid'));
            if (!hasEquip) {
                issues.push({
                    label: 'Groupes froids déclarés mais absents du patrimoine',
                    detail: 'Le client a coché "Groupes froids présents" — pense à les remonter côté patrimoine',
                    testid: 'coherence-issue-groupes-froids',
                });
            }
        }
        // Climatisation déclarée mais aucun équipement clim
        if (answers.climatisation_present === 'oui') {
            const hasEquip = equipment.some((e) => (e.category || '').toLowerCase().includes('clim'));
            if (!hasEquip) {
                issues.push({
                    label: 'Climatisation déclarée mais absente du patrimoine',
                    detail: 'Remontez les unités de climatisation observées sur site',
                    testid: 'coherence-issue-clim',
                });
            }
        }
        // Surface chauffée déclarée mais surface_tertiaire vide sur le site
        if (answers.surface_chauffee && !principal_site?.surface_tertiaire_m2) {
            issues.push({
                label: 'Surface tertiaire du site à compléter',
                detail: `Le client déclare une surface chauffée — renseignez surface_tertiaire`,
                testid: 'coherence-issue-surface',
            });
        }
        // Décret tertiaire "concerné" mais aucune attestation OPERAT
        if (answers.decret_tertiaire === 'concerne' && attestations.length === 0) {
            issues.push({
                severity: 'error',
                label: 'Décret Tertiaire déclaré mais aucune attestation OPERAT',
                detail: 'Récupérez l\'attestation officielle auprès du client',
                testid: 'coherence-issue-operat',
            });
        }
    }

    // Cohérence OPERAT : si valeurs Cref/Cabs manuelles ET attestations, on rappelle que
    // l'attestation prime (règle métier). Info non-bloquante.
    const declaredCrefManual = (bundle?.operat_declarations || []).some((o) => o.source === 'manuel' || o.source === 'declaratif');
    if (declaredCrefManual && attestations.length > 0) {
        issues.push({
            severity: 'warn',
            label: 'Cref/Cabs saisis manuellement et attestations OPERAT présentes',
            detail: 'Les valeurs OPERAT priment automatiquement sur la saisie manuelle',
            testid: 'coherence-issue-operat-vs-manual',
        });
    }

    return (
        <Card className="border-slate-200 bg-white" data-testid="energy-sources-card">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Layers className="w-4 h-4 text-slate-600" /> Sources de données & cohérence
                    </CardTitle>
                    {issues.length === 0 && !loading && (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="sources-all-good">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Sources cohérentes
                        </Badge>
                    )}
                    {issues.length > 0 && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="sources-has-issues">
                            <AlertTriangle className="w-3 h-3 mr-1" /> {issues.length} point{issues.length > 1 ? 's' : ''} à vérifier
                        </Badge>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Chips des 5 sources principales */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5 sm:gap-2">
                    <SourceChip
                        icon={qualifStatus.status === 'present' ? ClipboardCheck : ClipboardX}
                        label="Qualification client"
                        count={qualifStatus.count}
                        hint={qualifStatus.hint}
                        status={qualifStatus.status}
                        testid="source-chip-qualification"
                        onClick={() => scrollTo('chantier-qualif-block')}
                    />
                    <SourceChip
                        icon={Camera}
                        label="Relevé terrain"
                        count={studyStatus.count}
                        hint={studyStatus.hint}
                        status={studyStatus.status}
                        testid="source-chip-releve"
                        onClick={() => scrollTo('section-study-forms')}
                    />
                    <SourceChip
                        icon={FileCheck2}
                        label="OPERAT"
                        count={operatStatus.count}
                        hint={operatStatus.hint}
                        status={operatStatus.status}
                        testid="source-chip-operat"
                        onClick={() => scrollTo('energy-operat-section')}
                    />
                    <SourceChip
                        icon={FileText}
                        label="Études BE"
                        count={etudeStatus.count}
                        status={etudeStatus.status}
                        testid="source-chip-etudes"
                        onClick={() => scrollTo('chantier-docs-etude')}
                    />
                    <SourceChip
                        icon={FileSignature}
                        label="Devis"
                        count={devisStatus.count}
                        status={devisStatus.status}
                        testid="source-chip-devis"
                        onClick={() => scrollTo('chantier-docs-devis')}
                    />
                </div>

                {/* Résumé conso : source breakdown */}
                {readings.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-600 border-t pt-2" data-testid="readings-source-breakdown">
                        <span className="font-semibold text-slate-500 uppercase tracking-wide">Consommations :</span>
                        {['operat', 'facture', 'declaratif', 'estime'].map((src) => {
                            const n = readings.filter((r) => r.source === src && r.consumption_type === 'actual').length;
                            if (n === 0) return null;
                            return (
                                <span key={src} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
                                    <ClipboardList className="w-3 h-3" /> {n} {src}
                                </span>
                            );
                        })}
                    </div>
                )}

                {/* Incohérences détectées */}
                {issues.length > 0 && (
                    <div className="space-y-1.5 pt-1" data-testid="coherence-issues-list">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Cohérence à vérifier</div>
                        {issues.map((iss, i) => (
                            <CoherenceIssue key={i} {...iss} />
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
